import { defineStore } from "pinia";
import joi from "joi";
import { fromUnixToAPIDate, hoursToTime, toUnix } from "@/stories/utils/dates";
import { useAuthStore } from "@/stores/authStore";
import md5 from "md5";
import type { IMultiSelectItem } from "@/interfaces";
import type {
  StoreName,
  TabErrorSchemaTypes,
  IdType,
  RRuleSupportedEntityType,
  DefaultFilterType,
  DefaultState,
  ParticipantData,
  PreviewBasedState,
  DatePeriodSelection,
} from "@/types";
import helpers from "@/helpers";
import {
  type SummarizedMealsFragment,
  type MealInput,
  Module_Type,
  type CompactInstitutionFragment,
  type MealFragment,
} from "@/graphql/generated-types";
import { fixValidationFields } from "@/helpers/forms/validate";
import { globali18n } from "@/i18n";
import PagerManager from "@/helpers/manager/PagerManager";
import api from "@/api";
import getMaxDescriptionLength from "@/helpers/environment/getMaxDescriptionLength";
import {
  PDF_FEATURE,
  type PDFFeatureState,
} from "@/helpers/features/PDFFeature";
import {
  SIGNUP_FEATURE,
  type SignupFeatureState,
} from "@/helpers/features/SignupFeature";
import {
  SHARING_FEATURE,
  type SharingFeatureState,
  type DatabaseModel,
} from "@/helpers/features/SharingFeature";
import {
  RRULE_FEATURE,
  type RRuleFeatureState,
} from "@/helpers/features/RRuleFeature";
import {
  PICTURES_FEATURE,
  type PicturesFeatureState,
} from "@/helpers/features/PicturesFeature";
import ParticipantFeature, {
  type ParticipantFeatureState,
} from "@/helpers/features/ParticipantFeature";
import {
  EXTERNAL_EVENT_FEATURE,
  type ExternalEventFeatureState,
} from "@/helpers/features/ExternalEventFeature";
import getTabCounts from "@/helpers/shared/getTabCounts";
import toPreviewBasedState from "@/helpers/shared/toPreviewBasedState";
import { addHours, set } from "date-fns";

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  description: joi
    .string()
    .max(getMaxDescriptionLength())
    .allow("", null)
    .optional(),
  startDate: joi.number().required(),
  endDate: joi.number().required().greater(joi.ref("startDate")),
  mealDishes: joi.array().min(1).max(4),
  ...SIGNUP_FEATURE.getValidationRules(),
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "endDate", "mealDishes"],
  pictures: [],
  pdf: [],
};

const participantFeature = new ParticipantFeature<ParticipantData>([
  "employeesResponsible",
  "employeesSignedUp",
  "residentsResponsible",
  "residentsSignedUp",
]);

export type MealItem = {
  __typename: RRuleSupportedEntityType;
  id?: number;
  title: string;
  description: string;
  startDate: number;
  endDate: number;
  isCancelled: boolean;
  mealDishes: IMultiSelectItem[];
  institution?: CompactInstitutionFragment;
  original: Partial<MealItem>;
} & PDFFeatureState &
  SignupFeatureState &
  Omit<SharingFeatureState, "original"> &
  Omit<RRuleFeatureState, "original"> &
  PicturesFeatureState &
  ParticipantFeatureState<ParticipantData>;

export type MealsListFilters = {
  day: PreviewBasedState<string[]>;
  departments: PreviewBasedState<string[]>;
  groups: PreviewBasedState<string[]>;
  period: PreviewBasedState<DatePeriodSelection>;
};

export type MealsState = {
  listFilters: MealsListFilters;
  item: MealItem;
} & DefaultState &
  ExternalEventFeatureState<SummarizedMealsFragment>;

const getDefaultListFilters = (): MealsListFilters => {
  return {
    period: toPreviewBasedState({ period: {}, type: "days" }),
    day: toPreviewBasedState([]),
    departments: toPreviewBasedState([]),
    groups: toPreviewBasedState([]),
  };
};

const getDefaultState = (): MealsState => {
  const item: MealItem = {
    title: "",
    description: "",
    isCancelled: false,
    mealDishes: [],
    original: {},
    ...PDF_FEATURE.getDefault(),
    ...SIGNUP_FEATURE.getDefault(),
    ...SHARING_FEATURE.getDefault(),
    ...RRULE_FEATURE.getDefault("SingleMeal"),
    ...PICTURES_FEATURE.getDefault(),
    ...participantFeature.getDefault(),
  };

  return {
    listFilters: getDefaultListFilters(),
    tabErrors: {},
    originalHash: null,
    validation: null,
    pager: new PagerManager<SummarizedMealsFragment>(),
    item,
    listResourceType: "internal",
  };
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): MealsState => getDefaultState(),
    getters: {
      isSharedWithGroups: (state) =>
        SHARING_FEATURE.isSharedWithGroups(state.item),
      tabCounts: (state) => getTabCounts(state),
    },
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash(): string {
        const data = {
          ...this.getItem(),
          ...PDF_FEATURE.hash(this.item),
          ...SHARING_FEATURE.hash(this.item),
          ...RRULE_FEATURE.hash(this.item as RRuleFeatureState),
          ...PICTURES_FEATURE.hash(this.item),
        };

        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      async setItem(id: IdType) {
        // No need to refetch
        if (id && this.item.id?.toString() === id.toString()) {
          return;
        }

        let data: MealFragment | undefined;

        if (this.listResourceType === "internal") {
          data = await api.meals.getMealDetail(id);
        } else {
          data = await api.meals.getExternalMealDetail(id);
        }

        if (!data) {
          return;
        }

        this.item = {
          ...getDefaultState().item,
          id: parseInt(data.id),
          title: data.title,
          description: data.description || "",
          startDate: toUnix(new Date(data.startDate)),
          endDate: toUnix(new Date(data.endDate)),
          signupEnd: data.signupEnd ? toUnix(new Date(data.signupEnd)) : 0,
          isCancelled: data.cancelled,
          __typename: data.__typename as RRuleSupportedEntityType,
          institution: data.institution as CompactInstitutionFragment,
          mealDishes: (data.dishes || []).map((mealDish) => {
            return {
              id: mealDish.id,
              title: mealDish.title,
              imageURL: mealDish.picture?.source,
              imageID: mealDish.picture?.id,
              blurhash: mealDish.picture?.blurhash,
            } as IMultiSelectItem;
          }),
          ...PICTURES_FEATURE.getState(data),
          ...participantFeature.getItem(data),
        };

        // The favorite image is the image of the first dish. We shouldn't list
        // it in the pictures section.
        this.item.imageList = this.item.imageList.filter(
          (item) => item.isFavorite === false,
        );

        SHARING_FEATURE.setState({
          data: data as unknown as DatabaseModel,
          state: this.item,
          moduleType: Module_Type.Meal,
          listResourceType: this.listResourceType,
          onCompleted: this.setOriginalHash,
        });
        RRULE_FEATURE.setState(data, this.item as RRuleFeatureState);
        SIGNUP_FEATURE.setState(data, this.item);
        PDF_FEATURE.setState(data, this.item);

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): MealInput {
        const authStore = useAuthStore();
        const item = { ...this.item };

        const id = item?.id?.toString();

        const input: MealInput = {
          id,
          institution: authStore.institutionId,
          title: item.title,
          description: helpers.quill.editorModelToHtml(item.description),
          status: item.isCancelled ? 2 : 1,
          startDate: fromUnixToAPIDate(item.startDate),
          endDate: fromUnixToAPIDate(item.endDate),
          ...SIGNUP_FEATURE.getItem(this.item, { useNotifyNokOnSignup: false }),
          ...SHARING_FEATURE.getItem(this.item),
          ...RRULE_FEATURE.getItem(this.item as RRuleFeatureState),
        };

        participantFeature.append(input, this.item);

        return input;
      },
      async hasStaleData() {
        return await participantFeature.hasStaleData(this.item, () =>
          api.meals.getMealDetail(this.item.id),
        );
      },
      async isNotValid() {
        this.validation = helpers.forms.validate(VALIDATION_SCHEMA, {
          ...this.item,
          description: helpers.quill.editorModelToText(this.item.description),
          ...PDF_FEATURE.toValidation(this.item),
        });

        // We can't use usei18n() in stores.
        // https://stackoverflow.com/a/71449708
        const { t } = globali18n;

        // We should fix the validation labels
        fixValidationFields(this.validation, {
          startDate: t("global.start"),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async fetchItems(filter: DefaultFilterType) {
        await EXTERNAL_EVENT_FEATURE.fetchItems(
          this,
          filter,
          api.meals.getMeals,
          api.meals.getExternalMeals,
        );
      },
      setInstitutionDefaults() {
        const authStore = useAuthStore();

        const { timeDefault, signup, signupClose } =
          authStore.currentInstitution?.settings.mealSettings || {};

        if (timeDefault) {
          const [hours, minutes] = timeDefault.split(":").map(Number);
          const startDate = set(new Date(), {
            hours,
            minutes,
            seconds: 0,
            milliseconds: 0,
          });
          this.item.startDate = toUnix(startDate);
          this.item.endDate = toUnix(addHours(startDate, 1));
        }

        if (typeof signup === "boolean") {
          this.item.signup = signup;
        }

        if (signupClose) {
          this.item.signupCloseBefore = true;
          const { days, hours, minutes } = hoursToTime(signupClose);
          this.item.signupCloseDays = days;
          this.item.signupCloseHours = hours;
          this.item.signupCloseMinutes = minutes;
        }

        this.setOriginalHash();
      },
    },
  });
};

// View store
export const useMealsStore = createUseStore("MEALS");

// We use the same store structure for the create modal.
export const useAddMealsStore = createUseStore("ADD_MEALS");

export type MealsStore = ReturnType<typeof useMealsStore>;
